import React, { useEffect, useRef, useState } from "react";
import { CopyOutlined } from "@ant-design/icons";
import { Dropdown, Modal, Table, message } from "antd";
import html2canvas from "html2canvas";

import { MdOutlineDashboardCustomize } from "react-icons/md";
import { RiUnpinFill } from "react-icons/ri";
import { PinIcon } from "./Icons/PinIcon";
import { useDashboard } from "../contexts/DashboardProvider";
import { Formik } from "formik";
import { FiMoreVertical } from "react-icons/fi";
import { isString } from "lodash";

/**
 *
 * @param section mention the section name on which the graph is plotted
 * @param filters The filters applied to calculate the graph
 * @returns
 */
export default function TableComponent({ dataSource, menuButtonPosition = "outside", columns, enableScreenshot = true, enablePin = true, widgetUid = undefined, widgetIndex = undefined, widgetProps = {}, ...props }) {
    const [showMenu, setShowMenu] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    // submitted | completed | error
    const [formSubmissionState, setFormSubmissionState] = useState(undefined);
    const [cursorCoordinates, setCursorCoordinates] = useState({ x: null, y: null });
    const [boxDimensions, setBoxDimensions] = useState({});
    const ref = useRef(null);
    const { pinWidget, unPinwWidget } = useDashboard();
    const inputRef = useRef(null);

    const getImage = () => {
        return html2canvas(ref.current);
    };

    const copyImage = (imgCanvas, { extension = "png" } = {}) => {
        imgCanvas.toBlob((imgBlob) => {
            navigator.clipboard.write([new window.ClipboardItem({ [imgBlob.type]: imgBlob })]).then((res) => {
                message.success("The Table is copied to clipboard");
            });
        });
    };

    function onPinClick() {
        setShowPinModal(true);
        showMenu && setShowMenu(!showMenu);
    }

    function onUnPinClick() {
        if (isString(widgetUid)) {
            unPinwWidget(widgetUid);
        } else {
            throw Error("Widget does not exist");
        }
    }

    useEffect(() => {
        if (showPinModal && inputRef.current) {
            setTimeout(() => {
                inputRef.current.focus();
            }, 100);
        }
    }, [showPinModal]);

    const onSubmitWidgetForm = (formValues) => {
        setFormSubmissionState("Submitted");
        const { id, section, filters, ...metadata } = widgetProps;
        const widgetFilters = filters
            .filter((item) => item?.positiveAttributeFilter?.in?.values?.length > 0 || item?.measureValueFilter?.condition !== undefined)
            .reduce((acc, item) => {
                let filtervalue = item?.measureValueFilter?.condition || item?.positiveAttributeFilter?.in?.values;
                let filterIdentifier = item?.measureValueFilter?.measure?.localIdentifier || item?.positiveAttributeFilter?.displayForm?.identifier;
                acc = {
                    ...acc,
                    [filterIdentifier]: filtervalue,
                };
                return acc;
            }, {});

        pinWidget({
            label: id,
            section,
            metadata,
            filters: widgetFilters,
            ...formValues,
        })
            .then((res) => {
                message.success("Widget's pinned");
            })
            .catch((error) => {
                message.error(error);
            })
            .finally(() => {
                setFormSubmissionState("Completed");
                setShowPinModal(false);
            });
    };

    const menuItems = [
        enablePin
            ? {
                  key: 1,
                  icon: <PinIcon width={20} />,
                  label: "Pin to dashboard",
                  clickHandler: () => onPinClick(),
                  isenabled: true,
              }
            : {
                  key: 2,
                  icon: <RiUnpinFill width={20} />,
                  label: "Unpin widget",
                  clickHandler: () => onUnPinClick(),
                  isenabled: true,
              },
        {
            key: 3,
            icon: <CopyOutlined width={20} className="text-steel" />,
            label: "Copy to clipboard",
            clickHandler: () => {
                document.body.style.cursor = "wait";
                showMenu && setShowMenu(!showMenu);
                getImage().then((result) => {
                    document.body.style.cursor = "default";
                    copyImage(result);
                });
            },
            isenabled: enableScreenshot,
        },
    ];

    // Context menu position style
    let menuStyle = {};
    if (cursorCoordinates?.x > boxDimensions?.width * 0.8) {
        menuStyle = {
            ...menuStyle,
            right: boxDimensions.width - cursorCoordinates.x,
        };
    } else {
        menuStyle = {
            ...menuStyle,
            marginLeft: cursorCoordinates.x,
        };
    }

    if (cursorCoordinates?.y > boxDimensions?.height * 0.8) {
        menuStyle = {
            ...menuStyle,
            bottom: boxDimensions.height - cursorCoordinates.y,
        };
    } else {
        menuStyle = {
            ...menuStyle,
            marginTop: cursorCoordinates.y,
        };
    }

    return (
        <div className="relative">
            {enableScreenshot && (
                <Dropdown
                    menu={{
                        items: menuItems,
                        onClick: ({ key }) => {
                            menuItems.find((obj) => obj.key === Number(key))?.clickHandler();
                        },
                    }}
                    trigger={["click"]}
                >
                    <FiMoreVertical className={`absolute ${menuButtonPosition === "outside" ? "-top-12" : "top-2 "} right-2 z-40 rounded w-8 h-8 px-1.5 py-1 text-gray-500 cursor-pointer hover:bg-backgroundColor hover:text-indigo-700`} />
                </Dropdown>
            )}
            <div className="relative">
                <Modal open={showPinModal} onCancel={() => setShowPinModal(false)} footer={null}>
                    <div className="flex gap-2 items-center mb-2">
                        <MdOutlineDashboardCustomize className="w-8 h-8" color="#3a0ca3" />
                        <h3 className="my-0 text-indigo-700 font-bold text-xl">Add to my dashboard</h3>
                    </div>
                    <div className="flex gap-4">
                        <Formik initialValues={{ title: "" }} onSubmit={onSubmitWidgetForm}>
                            {({ handleChange, handleSubmit }) => (
                                <form className="flex flex-col gap-6 flex-1" onSubmit={handleSubmit}>
                                    <fieldset className="flex flex-col gap-1 pl-10">
                                        <label className="text-steel font-semibold text-base">Name</label>
                                        <input type="text" name="title" ref={inputRef} placeholder="Add a name" autoFocus={true} onChange={handleChange} className={`p-2 border rounded border-gray-300 focus:border-steel focus:outline-none`} />
                                    </fieldset>
                                    <button type="submit" disabled={formSubmissionState === "Submitted"} className="self-end bg-indigo-700 hover:bg-indigo-500 text-white font-semibold shadow-button py-2 rounded-md px-6">
                                        {formSubmissionState === "Submitted" ? "Saving ..." : "Save"}
                                    </button>
                                </form>
                            )}
                        </Formik>
                    </div>
                </Modal>

                <div ref={ref}>
                    <Table dataSource={dataSource} columns={columns} {...props} />
                </div>
            </div>
        </div>
    );
}
